import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Trash2,
  Eye,
  X,
  Check,
  AlertCircle,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader,
  Coins,
  TrendingUp,
  DollarSign,
  Activity,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

const getAuthToken = () => {
  return localStorage.getItem("accessToken") || "mock-token";
};

const CryptoManagement = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  const [activeTab, setActiveTab] = useState("coins");
  const [coins, setCoins] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
    itemId: null,
  });

  const [coinForm, setCoinForm] = useState({
    coingeckoId: "",
    tokenToCoinRate: "",
  });

  const [searchCoins, setSearchCoins] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchingCoins, setSearchingCoins] = useState(false);
  const [coinSearchValue, setCoinSearchValue] = useState("");
  const [selectedCoinMeta, setSelectedCoinMeta] = useState(null);

  const apiCall = async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          ...options.headers,
        },
      });

      const responseText = await response.text();
      let data = null;

      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          if (
            responseText.trim().startsWith("<!DOCTYPE") ||
            responseText.trim().startsWith("<html")
          ) {
            throw new Error(
              "API returned HTML instead of JSON. Check REACT_APP_BASE_URL and backend availability.",
            );
          }
          throw new Error("Invalid JSON response from API");
        }
      }

      if (!response.ok) {
        throw new Error(
          data?.message || `API request failed (${response.status})`,
        );
      }

      return data;
    } catch (err) {
      throw new Error(err.message || "Network error");
    }
  };

  // Fetch all coins
  const fetchCoins = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: pagination.limit.toString(),
        isAdmin: "true",
        ...(searchTerm && { search: searchTerm }),
      });

      const data = await apiCall(`/crypto/coins?${queryParams}`);
      setCoins(data.data || []);
      setPagination({
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 10,
        totalPages: Math.ceil((data.total || 0) / (data.limit || 10)),
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, pagination.limit]);

  // Search coins from CoinGecko
  const handleSearchCoins = async (query) => {
    if (!query || query.length < 2) {
      setSearchCoins([]);
      return;
    }

    try {
      setSearchingCoins(true);
      const data = await apiCall(`/crypto/search-coins?q=${query}`);
      setSearchCoins(data || []);
    } catch (err) {
      console.error("Search error:", err);
      setSearchCoins([]);
    } finally {
      setSearchingCoins(false);
    }
  };

  // Create coin
  const handleCreateCoin = async (e) => {
    e.preventDefault();

    if (!coinForm.coingeckoId || !coinForm.tokenToCoinRate) {
      setError("All fields are required");
      return;
    }

    try {
      setFormSubmitting(true);
      await apiCall("/crypto/coin/add", {
        method: "POST",
        body: JSON.stringify({
          coingeckoId: coinForm.coingeckoId,
          tokenToCoinRate: coinForm.tokenToCoinRate,
        }),
      });

      setSuccess("Coin added successfully!");
      setShowAddModal(false);
      setCoinForm({ coingeckoId: "", tokenToCoinRate: "" });
      setSearchCoins([]);
      setCoinSearchValue("");
      setSelectedCoinMeta(null);
      setShowSearchDropdown(false);
      fetchCoins();
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Delete coin
  const handleDeleteCoin = (id) => {
    setConfirmModal({
      show: true,
      title: "Delete Coin",
      message:
        "Are you sure you want to delete this coin? This action cannot be undone.",
      onConfirm: async () => {
        try {
          await apiCall(`/crypto/coin/${id}`, {
            method: "DELETE",
          });
          setSuccess("Coin deleted successfully!");
          setConfirmModal({
            show: false,
            title: "",
            message: "",
            onConfirm: null,
            itemId: null,
          });
          fetchCoins();
          setError(null);
        } catch (err) {
          setError(err.message);
        }
      },
      itemId: id,
    });
  };

  useEffect(() => {
    fetchCoins();
  }, [fetchCoins]);

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const themeClasses = {
    bg: isDarkMode ? "bg-black" : "bg-gray-100",
    text: isDarkMode ? "text-white" : "text-gray-900",
    card: isDarkMode ? "bg-black border-zinc-800" : "bg-white border-gray-200",
    input: isDarkMode
      ? "bg-zinc-800 border-zinc-700 text-white"
      : "bg-gray-100 border-gray-300 text-gray-900",
    hover: isDarkMode ? "hover:bg-zinc-800" : "hover:bg-gray-100",
  };

  if (loading && coins.length === 0) {
    return (
      <div className={`flex flex-col min-h-screen ${themeClasses.bg}`}>
        <div
          className={`flex-grow flex items-center justify-center ${themeClasses.text}`}
        >
          <div className="text-center">
            <Loader className="animate-spin w-12 h-12 mx-auto mb-4 text-orange-400" />
            <p className="text-xl">Loading crypto data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text}`}>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center">
              <Coins className="text-black" size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Crypto Management</h1>
              <p
                className={
                  isDarkMode ? "text-gray-400 mt-1" : "text-gray-600 mt-1"
                }
              >
                Manage cryptocurrencies and tokens
              </p>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 animate-in fade-in ${
              isDarkMode
                ? "bg-red-500/10 border-red-500/30 text-red-400"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{error}</div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 animate-in fade-in ${
              isDarkMode
                ? "bg-green-500/10 border-green-500/30 text-green-400"
                : "bg-green-50 border-green-200 text-green-700"
            }`}
          >
            <Check size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{success}</div>
            <button
              onClick={() => setSuccess(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className={`${themeClasses.card} border rounded-xl p-5 flex items-center gap-4`}
          >
            <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center">
              <Coins className="text-black" size={24} />
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Total Coins
              </p>
              <p className="text-2xl font-bold">{pagination.total}</p>
            </div>
          </div>

          <div
            className={`${themeClasses.card} border rounded-xl p-5 flex items-center gap-4`}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isDarkMode ? "bg-zinc-800" : "bg-gray-200"
              }`}
            >
              <TrendingUp
                className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                size={24}
              />
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Active Coins
              </p>
              <p className="text-2xl font-bold">
                {coins.filter((c) => c.isActive && !c.isDeleted).length}
              </p>
            </div>
          </div>

          <div
            className={`${themeClasses.card} border rounded-xl p-5 flex items-center gap-4`}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isDarkMode ? "bg-zinc-800" : "bg-gray-200"
              }`}
            >
              <DollarSign
                className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                size={24}
              />
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Page
              </p>
              <p className="text-2xl font-bold">
                {pagination.page} / {pagination.totalPages}
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div
          className={`flex gap-4 border-b ${
            isDarkMode ? "border-zinc-800" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => setActiveTab("coins")}
            className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "coins"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                  ? "border-transparent text-gray-400 hover:text-white"
                  : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <Coins size={20} />
            Coins
          </button>
        </div>

        {/* Coins Tab */}
        <div className="space-y-6">
          {/* Search and Add Button */}
          <div className="flex justify-between items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-64 relative">
              <Search
                size={18}
                className={`absolute left-3 top-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by coin name or symbol..."
                className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
              />
            </div>
            <button
              onClick={fetchCoins}
              className={`flex items-center gap-2 px-4 py-2 font-semibold transition-colors ${
                isDarkMode
                  ? "text-orange-400 hover:text-orange-300"
                  : "text-orange-600 hover:text-orange-700"
              }`}
              disabled={loading}
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={() => {
                setCoinForm({ coingeckoId: "", tokenToCoinRate: "" });
                setCoinSearchValue("");
                setSelectedCoinMeta(null);
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-6 py-2 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors shadow-lg"
            >
              <Plus size={20} />
              Add Coin
            </button>
          </div>

          {/* Coins Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {coins.length === 0 ? (
              <div
                className={`col-span-full ${themeClasses.card} border rounded-xl p-12 text-center`}
              >
                <Coins
                  size={48}
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                />
                <p
                  className={
                    isDarkMode ? "text-gray-400 mt-4" : "text-gray-600 mt-4"
                  }
                >
                  {searchTerm
                    ? "No coins found matching your search"
                    : "No coins found"}
                </p>
              </div>
            ) : (
              coins.map((coin) => (
                <div
                  key={coin._id}
                  className={`${themeClasses.card} border rounded-xl p-5 transition-all hover:shadow-lg`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1">
                      <img
                        src={coin.logo}
                        alt={coin.name}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/40";
                        }}
                      />
                      <div className="min-w-0">
                        <h3 className="font-semibold text-lg truncate">
                          {coin.name}
                        </h3>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          {coin.symbol}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        USD Price:
                      </span>
                      <span className="font-semibold">
                        ${coin.priceUsd?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Token Rate:
                      </span>
                      <span className="font-semibold text-orange-400">
                        {coin.tokenToCoinRate}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }
                      >
                        Status:
                      </span>
                      <span
                        className={
                          coin.isActive ? "text-green-400" : "text-red-400"
                        }
                      >
                        {coin.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-zinc-800">
                    <button
                      onClick={() => {
                        setSelectedCoin(coin);
                        setShowViewModal(true);
                      }}
                      className={`flex-1 p-2 rounded transition-colors ${themeClasses.hover}`}
                      title="View"
                    >
                      <Eye size={20} className="text-blue-400 mx-auto" />
                    </button>
                    <button
                      onClick={() => handleDeleteCoin(coin._id)}
                      className={`flex-1 p-2 rounded transition-colors ${themeClasses.hover}`}
                      title="Delete"
                    >
                      <Trash2 size={20} className="text-red-400 mx-auto" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {coins.length > 0 && (
            <div
              className={`flex items-center justify-center gap-4 mt-5 pt-5 border-t ${
                isDarkMode ? "border-zinc-800" : "border-gray-200"
              }`}
            >
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <ChevronLeft size={20} />
              </button>
              <span
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= pagination.totalPages}
                className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Add Coin Modal */}
        {showAddModal &&
          createPortal(
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div
              className={`${themeClasses.card} border rounded-2xl max-w-md w-full p-6`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Add New Coin</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setCoinForm({ coingeckoId: "", tokenToCoinRate: "" });
                    setSearchCoins([]);
                    setCoinSearchValue("");
                    setSelectedCoinMeta(null);
                  }}
                  className={`${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateCoin} className="space-y-4">
                {/* CoinGecko Search */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Search Coin *
                  </label>
                  <div className="relative">
                    {selectedCoinMeta && (
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none z-10">
                        <img
                          src={selectedCoinMeta.thumb}
                          alt={selectedCoinMeta.name}
                          className="w-5 h-5 rounded-full"
                        />
                      </div>
                    )}
                    <input
                      type="text"
                      value={coinSearchValue}
                      placeholder="Search CoinGecko (e.g., Bitcoin, Ethereum)..."
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        setCoinSearchValue(nextValue);
                        setSelectedCoinMeta(null);
                        handleSearchCoins(nextValue);
                      }}
                      onFocus={() => setShowSearchDropdown(true)}
                      className={`w-full py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input} ${
                        selectedCoinMeta ? "pl-10 pr-4" : "px-4"
                      }`}
                    />
                    {showSearchDropdown &&
                      (searchCoins.length > 0 || searchingCoins) && (
                        <div
                          className={`absolute top-full left-0 right-0 mt-1 rounded-lg border z-10 ${
                            isDarkMode
                              ? "bg-zinc-800 border-zinc-700"
                              : "bg-white border-gray-200"
                          } max-h-48 overflow-y-auto`}
                        >
                          {searchingCoins ? (
                            <div className="p-3 text-center">
                              <Loader
                                size={18}
                                className="animate-spin mx-auto"
                              />
                            </div>
                          ) : (
                            searchCoins.map((coin) => (
                              <button
                                key={coin.id}
                                type="button"
                                onClick={() => {
                                  setCoinForm({
                                    ...coinForm,
                                    coingeckoId: coin.id,
                                  });
                                  setCoinSearchValue(
                                    `${coin.name} (${coin.symbol?.toUpperCase() || ""})`,
                                  );
                                  setSelectedCoinMeta({
                                    thumb: coin.thumb,
                                    name: coin.name,
                                    symbol: coin.symbol,
                                  });
                                  setShowSearchDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2 hover:bg-orange-400/20 border-b last:border-b-0 transition-colors flex items-center gap-2`}
                              >
                                <img
                                  src={coin.thumb}
                                  alt={coin.name}
                                  className="w-5 h-5 rounded-full"
                                />
                                <div>
                                  <p className="font-medium text-white">
                                    {coin.name}
                                  </p>
                                  <p className="text-xs text-white/80">
                                    {coin.symbol}
                                  </p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                  </div>
                  {coinForm.coingeckoId && (
                    <p className="text-xs text-orange-400 mt-2">
                      Selected: {coinForm.coingeckoId}
                    </p>
                  )}
                </div>

                {/* Token to Coin Rate */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Token to Coin Rate *
                  </label>
                  <input
                    type="text"
                    value={coinForm.tokenToCoinRate}
                    onChange={(e) =>
                      setCoinForm({
                        ...coinForm,
                        tokenToCoinRate: e.target.value,
                      })
                    }
                    placeholder="e.g., 0.00000001"
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                  />
                  <p
                    className={`text-xs mt-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    How many coins equals 1 token
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setCoinForm({ coingeckoId: "", tokenToCoinRate: "" });
                      setSearchCoins([]);
                      setCoinSearchValue("");
                      setSelectedCoinMeta(null);
                    }}
                    disabled={formSubmitting}
                    className={`flex-1 px-4 py-2.5 rounded-lg font-medium border transition-colors disabled:opacity-50 ${
                      isDarkMode
                        ? "border-zinc-700 text-white hover:bg-zinc-800"
                        : "border-gray-300 text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      formSubmitting ||
                      !coinForm.coingeckoId ||
                      !coinForm.tokenToCoinRate
                    }
                    className="flex-1 px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {formSubmitting ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Add Coin
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
            document.body,
          )}

        {/* View Coin Modal */}
        {showViewModal &&
          selectedCoin &&
          createPortal(
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div
              className={`${themeClasses.card} border rounded-2xl max-w-lg w-full p-6`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Coin Details</h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedCoin(null);
                  }}
                  className={`${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex justify-center">
                  <img
                    src={selectedCoin.logo}
                    alt={selectedCoin.name}
                    className="w-32 h-32 rounded-xl object-cover shadow-lg"
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/128";
                    }}
                  />
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Coin Name
                  </h3>
                  <p className="text-lg font-semibold">{selectedCoin.name}</p>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Symbol
                  </h3>
                  <p className="text-lg font-semibold">{selectedCoin.symbol}</p>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Current USD Price
                  </h3>
                  <p className="text-lg font-semibold">
                    ${selectedCoin.priceUsd?.toFixed(2)}
                  </p>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Token to Coin Rate
                  </h3>
                  <p className="text-lg font-semibold text-orange-400">
                    {selectedCoin.tokenToCoinRate}
                  </p>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Status
                  </h3>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedCoin.isActive
                          ? isDarkMode
                            ? "bg-green-500/20 text-green-400"
                            : "bg-green-100 text-green-700"
                          : isDarkMode
                            ? "bg-red-500/20 text-red-400"
                            : "bg-red-100 text-red-700"
                      }`}
                    >
                      {selectedCoin.isActive ? "✓ Active" : "✗ Inactive"}
                    </span>
                  </div>
                </div>

                <div>
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    CoinGecko ID
                  </h3>
                  <p
                    className={`text-sm font-mono ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {selectedCoin.coingeckoId}
                  </p>
                </div>

                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedCoin(null);
                  }}
                  className="w-full px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
            document.body,
          )}

        {/* Delete Confirmation Modal */}
        {confirmModal.show &&
          createPortal(
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div
              className={`${themeClasses.card} border rounded-2xl max-w-md w-full p-6`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">{confirmModal.title}</h2>
                <button
                  onClick={() =>
                    setConfirmModal({
                      show: false,
                      title: "",
                      message: "",
                      onConfirm: null,
                      itemId: null,
                    })
                  }
                  className={`${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <p
                className={`mb-6 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {confirmModal.message}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() =>
                    setConfirmModal({
                      show: false,
                      title: "",
                      message: "",
                      onConfirm: null,
                      itemId: null,
                    })
                  }
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium border transition-colors ${
                    isDarkMode
                      ? "border-zinc-700 text-white hover:bg-zinc-800"
                      : "border-gray-300 text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default CryptoManagement;
